import { useMemo, useState, useCallback } from 'react';

/** Reusable selection state for any data-table.
 *  Returns:
 *    - selectedIds : Set<string>
 *    - isSelected(id)
 *    - toggle(id)
 *    - toggleAll(rows) — selects all if not all currently selected, else clears
 *    - clear()
 *    - allChecked / indeterminate — for the header checkbox
 *    - count
 *  The hook is stable across renders so consumers can safely destructure it. */
export function useBulkSelection(rows) {
  const [ids, setIds] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setIds(prev => {
      const allIds = (rows || []).map(r => r.id);
      const allChecked = allIds.length > 0 && allIds.every(x => prev.has(x));
      return allChecked ? new Set() : new Set(allIds);
    });
  }, [rows]);

  const clear = useCallback(() => setIds(new Set()), []);

  const isSelected = useCallback((id) => ids.has(id), [ids]);

  const { allChecked, indeterminate } = useMemo(() => {
    const total = (rows || []).length;
    const n = ids.size;
    return {
      allChecked: total > 0 && n === total,
      indeterminate: n > 0 && n < total,
    };
  }, [ids, rows]);

  return {
    selectedIds: ids,
    isSelected,
    toggle,
    toggleAll,
    clear,
    allChecked,
    indeterminate,
    count: ids.size,
  };
}
